<template>
  <router-view v-if="!loading"></router-view>
</template>

<script>
import Vue from 'vue'
import Arena from '@/models/setup/Arena'

import convertCase from '@/mixins/convert-case'
import isRole from '@/mixins/crud/computed/isRole'
import user from '@/mixins/crud/computed/user'
import ArenaCompany from '@/models/setup/ArenaCompany'
import TaxJurisdiction from '@/models/setup/TaxJurisdiction'

export default Vue.extend({
  mixins: [convertCase, isRole, user],

  data: () => ({
    loading: true,
  }),

  mounted() {
    if (this.$route.params.arena) {
      if (!this.$store.getters.arena && this.$store.getters.authenticated) {
        this.fetchArena(this.$route.params.arena)
      } else {
        this.loading = false
      }
    } else {
      this.loading = false
    }
  },

  methods: {
    async fetchArena(arenaId) {
      try {
        this.loading = true
        const response = await Arena.api.read(arenaId, 'include_fields=address')

        if (!response.data) {
          console.warn(`Arena not found.`)
          this.resetStateAndRedirectToDashboard()
          return
        }

        if (!this.isAdmin) {
          const employeeResponse = await Arena.api.getEmployees(
            response.data.id,
          )
          if (!Array.isArray(employeeResponse.data)) {
            console.warn(
              `Expected an array of employees, but got:`,
              employeeResponse.data,
            )
            this.resetStateAndRedirectToDashboard()
            return
          }
          if (!employeeResponse.data.includes(this.user.id)) {
            console.warn(`Insufficient permissions.`)
            this.resetStateAndRedirectToDashboard()
            return
          }
        }

        const data = response.data

        const [companyResponse, taxJurisdictionResponse] = await Promise.all([
          ArenaCompany.api.getCompany(data.id).catch(() => null),
          TaxJurisdiction.api
            .getArenaTaxJurisdiction(data.id)
            .catch(() => null),
        ])

        if (!companyResponse?.data) {
          console.warn(`Company not found.`)
        }
        if (!taxJurisdictionResponse?.data) {
          console.warn(`Tax jurisdiction not found.`)
        }

        const arenaData = {
          id: data.id,
          name: data.name,
          timezone: data.timezone,
          company: {
            ...(companyResponse?.data || {}),
            taxes: taxJurisdictionResponse?.data?.taxes || [],
          },
          currency: data.currency,
          locale: data.locale,
          coins_per_currency: data.coins_per_currency,
        }

        this.$store.commit('setArena', arenaData)
        const arenaCountry = data.address?.country_code?.toLowerCase() ?? 'us'
        this.$store.commit('setCountry', arenaCountry)
        const restricted = this.$route.matched
          .map((r) => r.meta?.restrictedCountries)
          .find((list) => Array.isArray(list) && list.includes(arenaCountry))
        if (restricted) {
          this.$router.replace('/statuses/403')
          return
        }
        localStorage.setItem('preferred_arena', arenaData.id)
        if (this.onlyArenaEntered()) {
          this.pushToDashboard(this.$router.currentRoute.path)
        }
      } catch (e) {
        if (e.response?.status !== 404) {
          console.warn(`Arena API failed.`)
          console.log(e.response)
        }
        this.resetStateAndRedirectToDashboard()
      } finally {
        this.loading = false
      }
    },
    pushToDashboard(arenaId) {
      this.$router.push(
        `${arenaId}/${
          this.isAdmin || this.isManager ? 'admin' : 'employee'
        }-dashboard`,
      )
    },
    onlyArenaEntered() {
      return this.$router.currentRoute.name === 'entities.arenas.entity'
    },
    resetStateAndRedirectToDashboard() {
      this.$store.commit('setArena', null)
      this.pushToDashboard('')
    },
  },
})
</script>
